import { Component } from '@theme/component';

const storageKey = 'ik-world-context';

class IkWorldProductContext extends Component {
  #controller;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    window.addEventListener('pageshow', this.#applyContext, { signal: this.#controller.signal });
    this.addEventListener('click', this.#rememberSelection, { signal: this.#controller.signal });
    const carousel = this.querySelector('[data-related-carousel]');
    carousel?.addEventListener('scroll', this.#updateCarousel, { passive: true, signal: this.#controller.signal });
    this.querySelector('[data-related-previous]')?.addEventListener('click', () => this.#scrollCarousel(-1), { signal: this.#controller.signal });
    this.querySelector('[data-related-next]')?.addEventListener('click', () => this.#scrollCarousel(1), { signal: this.#controller.signal });
    this.#resizeObserver?.disconnect();
    if (carousel) {
      this.#resizeObserver = new ResizeObserver(this.#updateCarousel);
      this.#resizeObserver.observe(carousel);
    }
    this.#applyContext();
    this.#updateCarousel();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#applyContext();
    this.#updateCarousel();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  #applyContext = () => {
    const options = Array.from(this.querySelectorAll('[data-world-option]'));
    const back = this.querySelector('[data-world-back]');
    const backLabel = this.querySelector('[data-world-back-label]');
    const breadcrumb = this.querySelector('[data-world-breadcrumb]');
    const breadcrumbLink = this.querySelector('[data-world-breadcrumb-link]');
    let saved;
    let referrer;
    try {
      saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    } catch {
      saved = null;
    }
    try {
      referrer = document.referrer ? new URL(document.referrer) : null;
    } catch {
      referrer = null;
    }

    let active;
    const collectionPath = location.pathname?.match(/^(.*\/collections\/[^/]+)\/products\/[^/]+\/?$/)?.[1];
    if (collectionPath) {
      active = options.find((option) => this.#matchesPath(option, collectionPath));
    } else if (referrer?.origin === location.origin) {
      active = options.find((option) => this.#matchesPath(option, referrer.pathname));
      if (!active && /\/products\//.test(referrer.pathname) && saved) {
        active = options.find((option) => option.dataset.worldHandle === saved.handle && this.#matchesPath(option, saved.path));
      }
    }

    if (back instanceof HTMLAnchorElement) {
      back.href = active ? active.dataset.worldPath : back.dataset.homePath;
      const accessibleLabel = active ? active.dataset.backLabel : back.dataset.homeLabel;
      back.setAttribute('aria-label', accessibleLabel);
      if (!backLabel) back.textContent = accessibleLabel;
    }
    // SEO v4: keep the server-rendered crumb (matches the BreadcrumbList) unless a world context is active.
    if (active && breadcrumb && breadcrumbLink instanceof HTMLAnchorElement) {
      breadcrumb.hidden = false;
      breadcrumbLink.href = active.dataset.worldPath;
      breadcrumbLink.textContent = active.dataset.worldName;
    }
    for (const option of options) option.hidden = true;

    if (this.dataset.position === 'bottom') {
      this.hidden = !this.querySelector('[data-related-selection]');
    }

    if (active) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          path: active.dataset.worldPath,
          name: active.dataset.worldName,
          handle: active.dataset.worldHandle,
        }));
      } catch {
        // The direct-referrer path works when browser storage is unavailable.
      }
    }
  };

  #updateCarousel = () => {
    const carousel = this.querySelector('[data-related-carousel]');
    const controls = this.querySelector('[data-related-controls]');
    if (!carousel || !controls) return;
    const maximum = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
    const position = Math.abs(carousel.scrollLeft);
    controls.hidden = maximum < 2;
    this.querySelector('[data-related-previous]').disabled = position < 2;
    this.querySelector('[data-related-next]').disabled = position >= maximum - 2;
  };

  #scrollCarousel(direction) {
    const carousel = this.querySelector('[data-related-carousel]');
    if (!carousel) return;
    const rtl = getComputedStyle(carousel).direction === 'rtl';
    carousel.scrollBy({
      left: carousel.clientWidth * direction * (rtl ? -1 : 1),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #rememberSelection = (event) => {
    const link = event.target?.closest?.('a[data-ik-world-product]');
    if (!link || !this.contains(link)) return;
    const row = link.closest('[data-related-selection]');
    if (!row) return;
    try {
      const path = row.dataset.worldPath;
      if (path && row.dataset.worldHandle && new URL(path, location.origin).origin === location.origin) {
        sessionStorage.setItem(storageKey, JSON.stringify({
          path,
          name: row.dataset.worldName,
          handle: row.dataset.worldHandle,
        }));
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch {
      // Collection product URLs retain their context when storage is unavailable.
    }
  };

  #matchesPath(option, path) {
    if (typeof path !== 'string') return false;
    try {
      const candidate = new URL(path, location.origin);
      return candidate.origin === location.origin &&
        [option.dataset.worldPath, option.dataset.worldCollectionPath].some((value) => {
          if (!value) return false;
          const expected = new URL(value, location.origin);
          return expected.origin === location.origin &&
            expected.pathname.replace(/\/$/, '') === candidate.pathname.replace(/\/$/, '');
        });
    } catch {
      return false;
    }
  }
}

if (!customElements.get('ik-world-product-context')) {
  customElements.define('ik-world-product-context', IkWorldProductContext);
}
